import React, { useState, useEffect } from "react";
import { TableBody, TableRow, TableCell } from "@material-ui/core";
import { Dimmer, Loader } from "semantic-ui-react";
import axios from "axios";
import moment from "moment";
import useTable from "../useTable";
import { Mensaje } from "../mensajes";
import { VerAuditoria } from "./modalVerAuditoria";
import { RestaurarAuditoria } from "./modalRestaurarAuditoria";
import {
  abrirVentanaReporte,
  mostrarReporteAuditoria,
} from "./reporteAuditoriaPDF";
import { camposCabeceraNota } from "./detalleNotaEntrega";

/*
 * Modulo de Auditoria.
 *
 * Las consultas se ejecutan con el endpoint generico de la API:
 *   GET /api/comun/seleccionarfunciones?funcion=<funcion>&parametros=<argumentos>
 * el cual ejecuta:  SELECT * FROM <funcion>(<argumentos>)
 *
 * Funciones de la base de datos
 * (el script de la tercera esta en public/sql/auditoria3_consultar.sql):
 *   fn_auditoria_consultar  -> tabla audit_log           (tabla, accion, desde, hasta, usuario uuid, registro text, limite)
 *   fn_auditoria2_consultar -> tabla auditoria           (tabla, accion, desde, hasta, usuario bigint, idtabla bigint, limite)
 *   fn_auditoria3_consultar -> tabla auditadataeliminada (documento, status, desde, hasta, usuario bigint, idauditoria bigint, limite)
 *
 * Los filtros se envian siempre en el mismo orden (ver armarParametros); cada
 * funcion los recibe con el significado que le corresponde.
 */
const FUNCION_LOG = "fn_auditoria_consultar";
const FUNCION_HISTORICA = "fn_auditoria2_consultar";
const FUNCION_ELIMINADOS = "fn_auditoria3_consultar";

/* Funcion de consulta que corresponde a cada pestaña */
const FUNCIONES = {
  log: FUNCION_LOG,
  historica: FUNCION_HISTORICA,
  eliminados: FUNCION_ELIMINADOS,
};

/* Funcion que devuelve las tablas de la base de datos (filtro TABLA) */
const FUNCION_TABLAS = "fn_auditoria_tablas_consultar";

/*
 * Funcion que devuelve la data completa (cabecera + detalle) de una nota de
 * entrega desde las tablas de respaldo (notaentregarespaldo +
 * notaentregadetallerespaldo). Se usa en la pestaña "Datos Eliminados" para
 * comparar el registro de auditoria con su respaldo
 * (ver public/sql/fn_notaentrega_respaldo_consultar.sql).
 */
const FUNCION_RESPALDO_NOTA = "fn_notaentrega_respaldo_consultar";

/*
 * Grupos de documento junto con su tabla de detalle. Al elegirlos se consultan
 * las dos tablas a la vez (las funciones de consulta aceptan varias tablas
 * separadas por coma). Solo se muestran los grupos cuyas tablas existan en la
 * base de datos.
 */
const GRUPOS_TABLAS = [
  {
    label: "NOTAS DE ENTREGA (+ detalle)",
    tablas: ["notaentrega", "notaentregadetalle"],
  },
  {
    label: "PEDIDOS (+ detalle)",
    tablas: ["pedido", "pedidodetalle"],
  },
  {
    label: "OPERACIONES DE INVENTARIO (+ detalle)",
    tablas: ["operacioninventario", "operacioninventariodetalle"],
  },
];

/*
 * Tablas que quedan seleccionadas por defecto en el filtro TABLA: factura de
 * compra junto con su detalle.
 */
const TABLA_POR_DEFECTO = {
  label: "FACTURAS DE COMPRA (+ detalle)",
  tablas: ["facturacompra", "facturacompra_detalle"],
};

/* Status de los documentos de la pestaña "Datos Eliminados" */
const ESTADOS_DOCUMENTO = [
  { value: "0", label: "PENDIENTE" },
  { value: "1", label: "PROCESADO" },
  { value: "4", label: "ANULADO" },
];

/* Icono y titulo del detalle/reporte segun la accion del registro */
const ACCIONES_ICONO = {
  INSERT: { icono: "fas fa-plus-circle", titulo: "Registro insertado" },
  UPDATE: { icono: "fas fa-edit", titulo: "Registro modificado" },
  DELETE: { icono: "fas fa-trash-alt", titulo: "Registro eliminado" },
};

/*
 * Filtros que cada pestaña NO muestra; su argumento se envia como NULL a la
 * funcion de consulta:
 *   - "Auditoría LOG":    no muestra USUARIO
 *   - "Datos Eliminados": no muestra REG. AUDITORÍA
 * (la pestaña "Auditoría" muestra todos los filtros)
 */
const FILTROS_OCULTOS = {
  log: { usuario: true },
  eliminados: { registro: true },
};

const ACCIONES = ["INSERT", "UPDATE", "DELETE"];

const TABS = [
  { key: "log", label: "Auditoría LOG", icono: "fas fa-history" },
  { key: "historica", label: "Auditoría", icono: "fas fa-clipboard-list" },
  { key: "eliminados", label: "Datos Eliminados", icono: "fas fa-trash-alt" },
];

const ESTILO_CABECERA = {
  backgroundColor: "rgba(5, 81, 130, 1)",
  color: "#ffffff",
  textAlign: "center",
};

const cabecera = (id, label) => ({ ...ESTILO_CABECERA, id, label });

const headCellsLog = [
  cabecera("1", "FECHA"),
  cabecera("2", "ACCIÓN"),
  cabecera("3", "TABLA"),
  cabecera("4", "REGISTRO"),
  cabecera("5", "USUARIO"),
  cabecera("6", "CAMPOS CAMBIADOS"),
  cabecera("opciones", "DETALLE"),
];

const headCellsHistorica = [
  cabecera("1", "FECHA"),
  cabecera("2", "ACCIÓN"),
  cabecera("3", "TABLA"),
  cabecera("4", "REGISTRO"),
  cabecera("5", "USUARIO"),
  cabecera("6", "CONCEPTO"),
  cabecera("opciones", "DETALLE"),
];

/* Columnas de la pestaña "Datos Eliminados" (tabla auditadataeliminada) */
const headCellsEliminados = [
  cabecera("1", "FECHA"),
  cabecera("2", "DOCUMENTO"),
  cabecera("3", "STATUS"),
  cabecera("4", "USUARIO"),
  cabecera("5", "REG. AUDITORÍA"),
  cabecera("opciones", "DETALLE"),
];

function Auditoria() {
  const op = require("../../../public/modulos/datos");
  const token = sessionStorage.getItem("jwtToken");

  const fechaSistema = moment(
    JSON.parse(sessionStorage.getItem("fechasistema")) || undefined
  );

  const [tab, setTab] = useState("log");
  const [activate, setActivate] = useState(false);
  const [records, setRecords] = useState([]);
  const [tablas, setTablas] = useState([]);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [detalle, setDetalle] = useState({
    campos: [],
    cambios: [],
    bloques: [],
  });
  /*
   * Columnas de comparacion (registro de auditoria vs. respaldo) que se muestran
   * lado a lado en el modal/reporte. Solo aplica a "Datos Eliminados" cuando el
   * documento es una nota de entrega con datos en las tablas de respaldo.
   */
  const [comparacion, setComparacion] = useState(null);
  /*
   * Registros eliminados (ACCIÓN = DELETE) con los que se arma el INSERT de
   * restauracion (ver modalRestaurarAuditoria).
   */
  const [mostrarRestaurar, setMostrarRestaurar] = useState(false);
  const [registrosRestaurar, setRegistrosRestaurar] = useState([]);
  const [filtros, setFiltros] = useState({
    tabla: TABLA_POR_DEFECTO.tablas.join(","),
    accion: "",
    desde: moment(fechaSistema).startOf("day").format("YYYY-MM-DD"),
    hasta: moment(fechaSistema).endOf("day").format("YYYY-MM-DD"),
    usuario: "",
    registro: "",
    limite: "1000",
  });
  const [mensaje, setMensaje] = useState({
    mostrar: false,
    titulo: "",
    texto: "",
    icono: "",
  });
  const [filterFn, setFilterFn] = useState({
    fn: (items) => {
      return items;
    },
  });

  /* Cabeceras de la tabla segun la pestaña activa */
  const headCells =
    tab === "log"
      ? headCellsLog
      : tab === "historica"
        ? headCellsHistorica
        : headCellsEliminados;

  const { TblContainer, TblHead, recordsAfterPagingAndSorting, TblPagination } =
    useTable(records, headCells, filterFn);

  /* Fecha del registro (fecha_ve = fecha de Venezuela) */
  const formatoFecha = (item) =>
    item && item.fecha_ve
      ? moment(item.fecha_ve).format("DD-MM-YYYY hh:mm A")
      : "";

  /* Quita comillas simples para no inyectar argumentos dentro de la funcion */
  const limpiar = (valor) =>
    String(valor === null || valor === undefined ? "" : valor)
      .replace(/'/g, "")
      .trim();

  /* Los argumentos se envian como literales SQL: NULL o 'texto' */
  const argumentoTexto = (valor) => {
    const limpio = limpiar(valor);
    return limpio === "" ? "NULL" : `'${limpio}'`;
  };

  const argumentoNumero = (valor, porDefecto) => {
    const limpio = limpiar(valor).replace(/[^0-9]/g, "");
    return limpio === "" ? porDefecto : limpio;
  };

  /*
   * Filtros que se envian a la funcion de consulta, siempre en este orden:
   *   1) tabla     -> TABLA (audit_log, auditoria)   | DOCUMENTO (auditadataeliminada)
   *   2) accion    -> ACCIÓN (audit_log, auditoria)  | STATUS (auditadataeliminada)
   *   3) desde        4) hasta        5) usuario
   *   6) registro  -> texto a buscar en el registro de auditoria (nro. de
   *                   documento o RIF). En audit_log se compara contra
   *                   registro_id, valor_anterior y valor_nuevo; en la tabla
   *                   auditoria contra nombretabla, concepto, usuario, idtabla
   *                   y data. La pestaña "Datos Eliminados" no lo envia.
   *   7) limite    (numerico)
   * Los seis primeros van como literales SQL ('texto' o NULL). El argumento de
   * los filtros que la pestaña no muestra se envia como NULL (FILTROS_OCULTOS).
   */
  const armarParametros = (f, tabUsar) => {
    const ocultos = FILTROS_OCULTOS[tabUsar || tab] || {};
    const valor = (clave) => (ocultos[clave] ? "" : f[clave]);

    return [
      argumentoTexto(valor("tabla")),
      argumentoTexto(valor("accion")),
      argumentoTexto(valor("desde")),
      argumentoTexto(valor("hasta")),
      argumentoTexto(valor("usuario")),
      argumentoTexto(valor("registro")),
      argumentoNumero(valor("limite"), "1000"),
    ].join(",");
  };

  /*
   * Tablas de la base de datos para el filtro TABLA.
   * Si la funcion no existe en la base de datos la lista queda vacia y el
   * filtro se muestra como caja de texto (ver el JSX del filtro).
   */
  const seleccionarTablas = () => {
    const endpoint = `${op.conexion}/api/comun/seleccionarfunciones?funcion=${FUNCION_TABLAS}&parametros=false`;

    axios
      .get(endpoint, {
        headers: {
          "x-access-token": `${token}`,
        },
      })
      .then((response) => {
        if (response.status === 200 && Array.isArray(response.data)) {
          setTablas(
            response.data
              .map((item) => item.tabla)
              .filter((tabla) => tabla !== null && tabla !== undefined)
          );
        }
      })
      .catch(() => {
        setTablas([]);
      });
  };

  const seleccionarRegistros = (filtrosUsar, tabUsar) => {
    const t = tabUsar || tab;
    const f = filtrosUsar || filtros;
    const funcion = FUNCIONES[t];
    const endpoint = `${op.conexion}/api/comun/seleccionarfunciones?funcion=${funcion}&parametros=${encodeURIComponent(
      armarParametros(f, t)
    )}`;

    setActivate(true);

    axios
      .get(endpoint, {
        headers: {
          "x-access-token": `${token}`,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          const filas = Array.isArray(response.data) ? response.data : [];
          setRecords(filas);

          /*
           * Si se busco un numero/RIF y no hay resultados se avisa, porque lo
           * mas comun es que el rango de fechas o la tabla elegida no incluyan
           * el movimiento que se busca.
           */
          const buscado = String(
            t === "eliminados" ? f.tabla : f.registro
          ).trim();

          if (filas.length === 0 && buscado !== "") {
            setMensaje({
              mostrar: true,
              titulo: "Sin resultados",
              texto:
                "No se encontró «" +
                buscado +
                "» con los filtros actuales. Revise que el rango DESDE/HASTA " +
                "incluya la fecha del movimiento y que la tabla seleccionada " +
                "sea la correcta (o elija TODAS).",
              icono: "informacion",
            });
          }
        }
      })
      .catch((error) => {
        setRecords([]);
        setMensaje({
          mostrar: true,
          titulo: "Error",
          texto:
            error.response === undefined
              ? "Error de Conexion"
              : error.response.data.message,
          icono: "error",
        });
      })
      .finally(() => {
        setActivate(false);
      });
  };

  const cambiarFiltro = (e) => {
    const { name, value } = e.target;
    setFiltros((anteriores) => ({ ...anteriores, [name]: value }));
  };

  /*
   * Los dos primeros filtros se comparten entre pestañas:
   *   - "Datos Eliminados": 1) numero de documento (texto libre), 2) status
   *   - las otras dos:      1) TABLA (select de la BD),        2) ACCIÓN (select)
   * Devuelve los filtros limpios para la pestaña indicada, de forma que al
   * cambiar de pestaña no se arrastre un valor que no aplica al destino.
   */
  const filtrosDeTab = (key) => {
    const filtrosTab = { ...filtros };

    if (key === "eliminados") {
      if (
        tablas.indexOf(filtrosTab.tabla) >= 0 ||
        filtrosTab.tabla.indexOf(",") >= 0
      ) {
        filtrosTab.tabla = "";
      }
      if (filtrosTab.accion !== "" && isNaN(Number(filtrosTab.accion))) {
        filtrosTab.accion = "";
      }
    } else {
      if (key === "historica") {
        /* La pestaña "Auditoría" solo consulta la factura de compra + detalle */
        filtrosTab.tabla = TABLA_POR_DEFECTO.tablas.join(",");
      } else if (
        tablas.length > 0 &&
        filtrosTab.tabla !== "" &&
        tablas.indexOf(filtrosTab.tabla) < 0 &&
        filtrosTab.tabla.indexOf(",") < 0
      ) {
        filtrosTab.tabla = "";
      }
      if (filtrosTab.accion !== "" && ACCIONES.indexOf(filtrosTab.accion) < 0) {
        filtrosTab.accion = "";
      }
    }

    return filtrosTab;
  };

  const cambiarTab = (key) => {
    setTab(key);
    setRecords([]);
    setComparacion(null);
    setFilterFn({
      fn: (items) => {
        return items;
      },
    });

    const filtrosTab = filtrosDeTab(key);
    if (
      filtrosTab.tabla !== filtros.tabla ||
      filtrosTab.accion !== filtros.accion
    ) {
      setFiltros(filtrosTab);
    }

    seleccionarRegistros(filtrosTab, key);
  };

  const handleSearch = (e) => {
    const valor = e.target.value.toUpperCase();

    setFilterFn({
      fn: (items) => {
        if (valor === "") {
          return items;
        }
        return items.filter((x) => {
          const texto = [
            x.accion,
            x.tabla_afectada,
            x.nombretabla,
            x.usuario,
            x.usuario_id,
            x.concepto,
            x.registro_id,
            x.idtabla,
            x.idauditoria,
            x.caja,
            x.numdocumento,
            x.status,
            x.hora,
            (x.campos_cambiados || []).join(" "),
          ]
            .join(" ")
            .toUpperCase();
          return texto.includes(valor);
        });
      },
    });
  };

  const texto = (valor) =>
    valor === null || valor === undefined ? "" : String(valor);

  const esObjeto = (valor) =>
    valor !== null && typeof valor === "object" && !Array.isArray(valor);

  /*
   * Los registros de auditoria pueden venir envueltos en el nombre de la tabla
   * o en el tipo de dato, por ejemplo:
   *   { data_nueva: { facturacompra: { numfactura: '8415332552', ... } } }
   *   { facturacompra: { numfactura: '8415332552', ... } }
   * Esta funcion quita esas envolturas (hasta 3 niveles) para poder comparar
   * los campos reales de la fila.
   */
  const desenvolver = (valor, nivel) => {
    const nivelActual = nivel || 0;

    if (nivelActual >= 3 || !esObjeto(valor)) {
      return valor;
    }

    const claves = Object.keys(valor);
    if (claves.length !== 1) {
      return valor;
    }

    const interno = valor[claves[0]];
    return esObjeto(interno) ? desenvolver(interno, nivelActual + 1) : valor;
  };

  /* Campos modificados del registro (valor anterior contra valor nuevo) */
  const cambiosDe = (item) => {
    const anterior = desenvolver(item.valor_anterior);
    const nuevo = desenvolver(item.valor_nuevo);

    if (!esObjeto(anterior) && !esObjeto(nuevo)) {
      return [];
    }

    const estaEn = (origen, clave) => esObjeto(origen) && clave in origen;

    /* claves que informa la funcion, descartando las de envoltura */
    let claves = (
      Array.isArray(item.campos_cambiados) ? item.campos_cambiados : []
    ).filter((clave) => estaEn(anterior, clave) || estaEn(nuevo, clave));

    if (claves.length === 0) {
      const unicas = {};
      [anterior, nuevo].forEach((valor) => {
        if (esObjeto(valor)) {
          Object.keys(valor).forEach((clave) => {
            unicas[clave] = true;
          });
        }
      });

      /* sin lista de la funcion, se muestran solo los campos que cambiaron */
      claves = Object.keys(unicas).filter(
        (clave) =>
          JSON.stringify(esObjeto(anterior) ? anterior[clave] : undefined) !==
          JSON.stringify(esObjeto(nuevo) ? nuevo[clave] : undefined)
      );
    }

    return claves.map((campo) => ({
      campo: campo,
      anterior: esObjeto(anterior) ? anterior[campo] : undefined,
      nuevo: esObjeto(nuevo) ? nuevo[campo] : undefined,
    }));
  };

  /*
   * Arma el detalle del registro (modal y reporte) segun la pestaña activa:
   * titulo, subtitulo, icono, datos, campos modificados y campos JSON.
   */
  const detalleDe = (item) => {
    const fecha = formatoFecha(item);
    const accion = texto(item.accion).toUpperCase();
    const estilo =
      ACCIONES_ICONO[accion] ||
      { icono: "fas fa-history", titulo: "Registro de auditoría" };

    if (tab === "log") {
      const cambios = cambiosDe(item);

      return {
        registro: item,
        titulo: estilo.titulo + " en " + texto(item.tabla_afectada),
        subtitulo: "audit_log · " + fecha,
        icono: estilo.icono,
        campos: [
          { label: "Fecha", valor: fecha },
          { label: "Acción", valor: item.accion },
          { label: "Tabla", valor: item.tabla_afectada },
          { label: "Registro", valor: item.registro_id },
          { label: "Usuario", valor: item.usuario_id },
          {
            label: "Campos cambiados",
            valor: cambios.map((cambio) => cambio.campo).join(", "),
          },
        ],
        cambios: cambios,
        bloques: [
          { label: "Valor anterior", valor: item.valor_anterior },
          { label: "Valor nuevo", valor: item.valor_nuevo },
        ],
      };
    }

    if (tab === "historica") {
      return {
        registro: item,
        titulo: "Registro en " + texto(item.nombretabla),
        subtitulo: "auditoria · " + fecha,
        icono: estilo.icono,
        campos: [
          { label: "Fecha", valor: fecha },
          { label: "Acción", valor: item.accion },
          { label: "Tabla", valor: item.nombretabla },
          { label: "Registro", valor: item.idtabla },
          { label: "Usuario", valor: item.usuario },
          { label: "Caja", valor: item.caja },
          { label: "Concepto", valor: item.concepto },
        ],
        cambios: [],
        bloques: [{ label: "Datos", valor: item.data }],
      };
    }

    return {
      registro: item,
      titulo: "Documento eliminado " + texto(item.numdocumento),
      subtitulo: "auditadataeliminada · " + fecha,
      icono: "fas fa-trash-alt",
      campos: [
        { label: "Fecha", valor: fecha },
        { label: "Hora", valor: item.hora },
        { label: "Documento", valor: item.numdocumento },
        { label: "Status", valor: etiquetaStatus(item.status) },
        { label: "Usuario", valor: item.idusuario },
        { label: "Reg. auditoría", valor: item.idauditoria },
      ],
      cambios: [],
      bloques: [],
    };
  };

  /*
   * Consulta la data completa de una nota de entrega en las tablas de respaldo
   * (notaentregarespaldo + notaentregadetallerespaldo) por su numero de
   * documento. Devuelve una promesa con el arreglo de coincidencias (puede
   * venir vacio si el documento no es una nota de entrega o no tiene respaldo).
   */
  const consultarRespaldoNota = (numdocumento) => {
    const documento = limpiar(numdocumento);
    if (documento === "") {
      return Promise.resolve([]);
    }

    const endpoint = `${op.conexion}/api/comun/seleccionarfunciones?funcion=${FUNCION_RESPALDO_NOTA}&parametros=${encodeURIComponent(
      argumentoTexto(documento)
    )}`;

    return axios
      .get(endpoint, { headers: { "x-access-token": `${token}` } })
      .then((response) =>
        response.status === 200 && Array.isArray(response.data)
          ? response.data
          : []
      )
      .catch(() => []);
  };

  /*
   * Arma la comparacion de un documento eliminado: el primer elemento es el
   * registro de auditoria (tipo "auditoria") y los siguientes son cada
   * coincidencia del respaldo de la nota de entrega (tipo "notaentrega", con su
   * cabecera `nota` y sus renglones `detalle`). El modal y el reporte dibujan
   * el ticket con esos renglones. La funcion SQL ya devuelve una sola fila por
   * idnotaentrega y una linea por producto, de modo que cada nota se muestra una
   * vez con sus productos (sin repetirlos).
   * Devuelve null si no hay respaldo que comparar.
   */
  const comparacionDe = (item, filas) => {
    if (!Array.isArray(filas) || filas.length === 0) {
      return null;
    }

    const columnas = [
      {
        tipo: "auditoria",
        titulo: "Documento eliminado (auditoría)",
        subtitulo: "auditadataeliminada",
        icono: "fas fa-trash-alt",
        campos: [
          { label: "Fecha", valor: formatoFecha(item) },
          { label: "Hora", valor: item.hora },
          { label: "Documento", valor: item.numdocumento },
          { label: "Status", valor: etiquetaStatus(item.status) },
          { label: "Usuario", valor: item.idusuario },
          { label: "Reg. auditoría", valor: item.idauditoria },
        ],
        bloques: [],
      },
    ];

    filas.forEach((fila, indice) => {
      const numero = texto(fila.numnotaentrega).trim();
      columnas.push({
        tipo: "notaentrega",
        titulo: "Nota de entrega (respaldo " + (indice + 1) + ")",
        subtitulo:
          "notaentregarespaldo" + (numero === "" ? "" : " · N° " + numero),
        icono: "fas fa-file-invoice",
        nota: fila.nota,
        campos: camposCabeceraNota(fila.nota),
        detalle: fila.detalle,
      });
    });

    return columnas;
  };

  const hanVer = (item) => (e) => {
    e.preventDefault();
    setDetalle(detalleDe(item));
    setComparacion(null);
    setMostrarDetalle(true);

    /* En "Datos Eliminados" se busca el respaldo del documento para comparar */
    if (tab === "eliminados") {
      consultarRespaldoNota(item.numdocumento).then((filas) =>
        setComparacion(comparacionDe(item, filas))
      );
    }
  };

  /* Datos de la empresa para el encabezado del reporte */
  const metadatosReporte = () => {
    const logo = JSON.parse(sessionStorage.getItem("logo"));
    return {
      empresa: JSON.parse(sessionStorage.getItem("organizacion")),
      rif: JSON.parse(sessionStorage.getItem("riforganizacion")),
      usuario: JSON.parse(sessionStorage.getItem("login")),
      urlLogo: logo ? `${op.conexion}/imagen?img=athos/organizacion/${logo}` : "",
    };
  };

  /*
   * Genera y muestra el reporte (un registro = detalle, varios = listado).
   * La ventana se abre en el clic para que el navegador no la bloquee y el
   * reporte se imprima/guarde desde el visor de PDF del navegador.
   */
  const imprimirReporte = (
    registros,
    detalleRegistro,
    ventanaAbierta,
    comparacionUsar
  ) => {
    const ventana = ventanaAbierta || abrirVentanaReporte();
    setActivate(true);
    mostrarReporteAuditoria(
      {
        tipo: tab,
        registros: registros,
        filtros: filtros,
        detalle: detalleRegistro,
        comparacion: comparacionUsar || null,
        metadatos: metadatosReporte(),
      },
      ventana
    )
      .catch((error) => {
        setMensaje({
          mostrar: true,
          titulo: "Error",
          texto: error.message,
          icono: "error",
        });
      })
      .finally(() => {
        setActivate(false);
      });
  };

  const hanImprimir = (item) => (e) => {
    e.preventDefault();
    const ventana = abrirVentanaReporte();
    const detalleItem = detalleDe(item);

    /* Las otras pestañas no tienen respaldo con que comparar */
    if (tab !== "eliminados") {
      imprimirReporte([item], detalleItem, ventana, null);
      return;
    }

    consultarRespaldoNota(item.numdocumento).then((filas) =>
      imprimirReporte([item], detalleItem, ventana, comparacionDe(item, filas))
    );
  };

  /* Imprime el listado que se esta viendo (ya filtrado por el buscador) */
  const imprimirListado = () => {
    const lista = filterFn.fn(records);
    if (lista.length === 0) {
      setMensaje({
        mostrar: true,
        titulo: "Notificación",
        texto: "¡ No hay registros para imprimir !",
        icono: "informacion",
      });
      return;
    }
    imprimirReporte(lista, null);
  };

  /*
   * Abre el modal para restaurar los registros eliminados del listado actual
   * (filtro ACCIÓN = DELETE). El modal muestra el valor anterior de cada uno y
   * arma el INSERT para volver a cargarlos en la tabla afectada.
   */
  const restaurarEliminados = () => {
    const lista = filterFn
      .fn(records)
      .filter((item) => texto(item.accion).toUpperCase() === "DELETE");

    if (lista.length === 0) {
      setMensaje({
        mostrar: true,
        titulo: "Notificación",
        texto: "¡ No hay registros eliminados para restaurar !",
        icono: "informacion",
      });
      return;
    }

    setRegistrosRestaurar(lista);
    setMostrarRestaurar(true);
  };

  useEffect(() => {
    seleccionarTablas();
    seleccionarRegistros();
  }, []);

  /*
   * Grupos de documento + detalle. El de factura de compra va siempre primero
   * porque es el que queda seleccionado por defecto; los demás se muestran solo
   * si sus tablas existen en la base de datos.
   */
  const gruposTablas = [TABLA_POR_DEFECTO].concat(
    GRUPOS_TABLAS.map((grupo) => ({
      label: grupo.label,
      tablas: grupo.tablas.filter((tabla) => tablas.indexOf(tabla) >= 0),
    })).filter((grupo) => grupo.tablas.length > 0)
  );

  /*
   * Opciones del select TABLA. La pestaña "Auditoría" solo permite la factura de
   * compra + su detalle; en "Auditoría LOG" se muestran los grupos y todas las
   * tablas de la base de datos.
   */
  const gruposFiltro = tab === "historica" ? [TABLA_POR_DEFECTO] : gruposTablas;
  const tablasFiltro = tab === "historica" ? [] : tablas;

  /* Etiquetas de los filtros que cambian segun la pestaña */
  const etiquetaFiltro1 = tab === "eliminados" ? "DOCUMENTO:" : "TABLA:";
  const marcadorFiltro1 = tab === "eliminados" ? "numdocumento" : "nombretabla";
  const etiquetaFiltro2 = tab === "eliminados" ? "STATUS:" : "ACCIÓN:";

  /* Etiqueta del status: 0 = PENDIENTE, 1 = PROCESADO, 4 = ANULADO */
  const etiquetaStatus = (valor) => {
    const estado = ESTADOS_DOCUMENTO.filter(
      (item) => item.value === String(valor)
    )[0];
    return estado ? estado.label : valor;
  };

  /*
   * Valores de cada fila segun la pestaña. Los filtros se envian por posicion
   * a la funcion de consulta (ver armarParametros).
   */
  const celdasFila = (item) => {
    if (tab === "log") {
      return [
        formatoFecha(item),
        item.accion,
        item.tabla_afectada,
        item.registro_id,
        item.usuario_id,
        cambiosDe(item)
          .map((cambio) => cambio.campo)
          .join(", "),
      ];
    }
    if (tab === "historica") {
      return [
        formatoFecha(item),
        item.accion,
        item.nombretabla,
        item.idtabla,
        item.usuario,
        item.concepto,
      ];
    }
    return [
      formatoFecha(item),
      item.numdocumento,
      etiquetaStatus(item.status),
      item.idusuario,
      item.idauditoria,
    ];
  };

  const estiloCelda = {
    textAlign: "center",
    padding: "8px 4px",
    fontSize: "12px",
  };

  return (
    <div className="container-fluid rounded">
      <div className="container-fluid">
        <div className="card">
          <Dimmer active={activate} inverted>
            <Loader inverted>cargando...</Loader>
          </Dimmer>

          <VerAuditoria
            show={mostrarDetalle}
            datos={detalle.registro}
            titulo={detalle.titulo}
            subtitulo={detalle.subtitulo}
            icono={detalle.icono}
            campos={detalle.campos}
            cambios={detalle.cambios}
            bloques={detalle.bloques}
            comparacion={comparacion}
            onImprimir={() =>
              imprimirReporte([detalle.registro], detalle, null, comparacion)
            }
            onHideCancela={() => setMostrarDetalle(false)}
          />

          <RestaurarAuditoria
            show={mostrarRestaurar}
            registros={registrosRestaurar}
            onHideCancela={() => setMostrarRestaurar(false)}
          />

          <div className="container">
            <div className="content">
              <div className="container">
                <div className="col-md-12 row m-0 pt-3">
                  <ul
                    className="nav nav-tabs mb-2"
                    id="tabsAuditoria"
                    role="tablist"
                  >
                    {TABS.map((t) => (
                      <li className="nav-item" key={t.key}>
                        <button
                          type="button"
                          className={`nav-link py-2 ${tab === t.key ? "active" : ""
                            }`}
                          onClick={() => cambiarTab(t.key)}
                        >
                          <i className={`${t.icono} me-2`}></i>
                          {t.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="col-md-12 row m-0 py-2">
                    <div className="col-md-4 px-1 mb-2">
                      <div className="input-group input-group-sm">
                        <label
                          className="input-group-text"
                          id="inputGroup-sizing-sm"
                        >
                          {etiquetaFiltro1}
                        </label>
                        {tablas.length > 0 && tab !== "eliminados" ? (
                          <select
                            name="tabla"
                            value={filtros.tabla}
                            onChange={cambiarFiltro}
                            className="form-select form-select-sm text-uppercase"
                          >
                            {tab !== "historica" && (
                              <option value="">TODAS</option>
                            )}
                            <optgroup label="Documento + detalle">
                              {gruposFiltro.map((grupo, index) => (
                                <option
                                  value={grupo.tablas.join(",")}
                                  key={index}
                                >
                                  {grupo.label}
                                </option>
                              ))}
                            </optgroup>
                            {tablasFiltro.length > 0 && (
                              <optgroup label="Tablas">
                                {tablasFiltro.map((tabla, index) => (
                                  <option value={tabla} key={index}>
                                    {tabla}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                        ) : (
                          <input
                            autoComplete="off"
                            type="text"
                            name="tabla"
                            value={filtros.tabla}
                            onChange={cambiarFiltro}
                            placeholder={marcadorFiltro1}
                            className="form-control text-uppercase"
                            aria-label="Sizing example input"
                            aria-describedby="inputGroup-sizing-sm"
                          />
                        )}
                      </div>
                    </div>

                    <div className="col-md-4 px-1 mb-2">
                      <div className="input-group input-group-sm">
                        <label
                          className="input-group-text"
                          id="inputGroup-sizing-sm"
                        >
                          {etiquetaFiltro2}
                        </label>
                        {tab === "eliminados" ? (
                          <select
                            name="accion"
                            value={filtros.accion}
                            onChange={cambiarFiltro}
                            className="form-select form-select-sm text-uppercase"
                          >
                            <option value="">TODOS</option>
                            {ESTADOS_DOCUMENTO.map((estado, index) => (
                              <option value={estado.value} key={index}>
                                {estado.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <select
                            name="accion"
                            value={filtros.accion}
                            onChange={cambiarFiltro}
                            className="form-select form-select-sm text-uppercase"
                          >
                            <option value="">TODAS</option>
                            {ACCIONES.map((accion, index) => (
                              <option value={accion} key={index}>
                                {accion}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    {tab !== "eliminados" && (
                      <div className="col-md-4 px-1 mb-2">
                        <div className="input-group input-group-sm">
                          <label
                            className="input-group-text"
                            id="inputGroup-sizing-sm"
                          >
                            NÚMERO / RIF:
                          </label>
                          <input
                            autoComplete="off"
                            type="text"
                            name="registro"
                            value={filtros.registro}
                            onChange={cambiarFiltro}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") seleccionarRegistros();
                            }}
                            placeholder="nro. doc. o RIF"
                            title="Busca este valor dentro del registro de auditoría: numero de nota de entrega, factura de compra, pedido, registro de pago o RIF del agente/persona"
                            className="form-control"
                            aria-label="Sizing example input"
                            aria-describedby="inputGroup-sizing-sm"
                          />
                        </div>
                      </div>
                    )}

                    {tab !== "log" && (
                      <div className="col-md-4 px-1 mb-2">
                        <div className="input-group input-group-sm">
                          <label
                            className="input-group-text"
                            id="inputGroup-sizing-sm"
                          >
                            USUARIO:
                          </label>
                          <input
                            autoComplete="off"
                            type="text"
                            name="usuario"
                            value={filtros.usuario}
                            onChange={cambiarFiltro}
                            placeholder="id del usuario"
                            className="form-control"
                            aria-label="Sizing example input"
                            aria-describedby="inputGroup-sizing-sm"
                          />
                        </div>
                      </div>
                    )}
                    <div className="col-md-4 px-1 mb-2">
                      <div className="input-group input-group-sm">
                        <label
                          className="input-group-text"
                          id="inputGroup-sizing-sm"
                        >
                          DESDE:
                        </label>
                        <input
                          type="date"
                          name="desde"
                          value={filtros.desde}
                          onChange={cambiarFiltro}
                          className="form-control"
                          aria-label="Sizing example input"
                          aria-describedby="inputGroup-sizing-sm"
                        />
                      </div>
                    </div>

                    <div className="col-md-4 px-1 mb-2">
                      <div className="input-group input-group-sm">
                        <label
                          className="input-group-text"
                          id="inputGroup-sizing-sm"
                        >
                          HASTA:
                        </label>
                        <input
                          type="date"
                          name="hasta"
                          value={filtros.hasta}
                          onChange={cambiarFiltro}
                          className="form-control"
                          aria-label="Sizing example input"
                          aria-describedby="inputGroup-sizing-sm"
                        />
                      </div>
                    </div>

                    <div className="col-md-3 px-1 mb-2">
                      <div className="input-group input-group-sm">
                        <label
                          className="input-group-text"
                          id="inputGroup-sizing-sm"
                        >
                          LÍMITE:
                        </label>
                        <input
                          autoComplete="off"
                          type="number"
                          min="1"
                          name="limite"
                          value={filtros.limite}
                          onChange={cambiarFiltro}
                          className="form-control text-end"
                          aria-label="Sizing example input"
                          aria-describedby="inputGroup-sizing-sm"
                        />
                      </div>
                    </div>

                    <div className="col-md-3 px-1 mb-2 d-flex align-items-center">
                      <button
                        onClick={() => seleccionarRegistros()}
                        disabled={activate}
                        className="btn btn-sm btn-primary rounded-pill px-4"
                      >
                        <i className="fas fa-search"> CONSULTAR</i>
                      </button>
                      <button
                        onClick={imprimirListado}
                        disabled={activate}
                        className="btn btn-sm btn-outline-secondary rounded-pill px-3 ms-1"
                        title="Imprimir el listado de registros consultados"
                      >
                        <i className="fas fa-print"></i>
                      </button>
                      {filtros.accion === "DELETE" && (
                        <button
                          onClick={restaurarEliminados}
                          disabled={activate}
                          className="btn btn-sm btn-outline-danger rounded-pill px-3 ms-1"
                          title="Armar el INSERT de los registros eliminados (valor anterior)"
                        >
                          <i className="fas fa-trash-restore"></i>
                        </button>
                      )}
                    </div>

                    <div className="col-md-4 px-1 mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm rounded-pill"
                        onChange={handleSearch}
                        placeholder="Buscar"
                      />
                    </div>

                    <div className="col-md-2 px-1 mb-2 d-flex align-items-center justify-content-end">
                      <span className="badge bg-secondary">
                        Registros: {records.length}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <TblContainer>
                      <TblHead />

                      <TableBody>
                        {records &&
                          recordsAfterPagingAndSorting().map((item, index) => (
                            <TableRow key={index} style={{ padding: "0" }}>
                              {celdasFila(item).map((valor, i) => (
                                <TableCell
                                  className="align-baseline text-break"
                                  style={estiloCelda}
                                  key={i}
                                >
                                  {valor === null || valor === undefined
                                    ? ""
                                    : valor}
                                </TableCell>
                              ))}

                              <TableCell
                                className="align-baseline"
                                style={{ textAlign: "center", width: "110px" }}
                              >
                                <button
                                  onClick={hanVer(item)}
                                  className="btn btn-sm btn-info rounded-circle me-1"
                                  title="Ver detalle"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                                <button
                                  onClick={hanImprimir(item)}
                                  className="btn btn-sm btn-secondary rounded-circle"
                                  title="Imprimir reporte"
                                >
                                  <i className="fas fa-print"></i>
                                </button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </TblContainer>
                    <TblPagination />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Mensaje
            mensaje={mensaje}
            onHide={() =>
              setMensaje({ mostrar: false, titulo: "", texto: "", icono: "" })
            }
          />
        </div>
      </div>
    </div>
  );
}

export default Auditoria;



// WEBPACK FOOTER //
// ./src/components/configuracionGeneral/auditoria.js